import { selectMiniPieBackground } from "./miniChartSelectors";
// frontend/src/components/mcbim/RoomColoringResultChart.tsx
import React, { useState } from "react";
import "./RoomColoringResultChart.css";
import { Card, Stack, Text, Group, Box, Divider, Flex } from "@mantine/core";
import { useAppSelector } from "../app/hooks";
import { selectActiveRoomColoring } from "../Room/roomSelectors";
import { RoomColoringPieChart } from "./RoomColoringPieChart";
import { RoomColoringTrendChart } from "./RoomColoringTrendChart";
import {
  selectRoomColoringChartData,
  selectTrendByKeyArgsForRoomColoring,
} from "./roomColoringSelectors";

const COLLAPSED_KEY = "multiplacer.roomCharts.collapsed";
export const RoomColoringResultChart: React.FC<{
  style?: React.CSSProperties;
  collapsible?: boolean;
}> = ({ style, collapsible = false }) => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) !== "false";
    } catch {
      return true;
    }
  });
  const toggle = () =>
    setCollapsed((current) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, String(!current));
      } catch {
        /* Storage is optional. */
      }
      return !current;
    });
  const chart = useAppSelector(selectRoomColoringChartData);
  const { totalCount } = chart;
  const miniPieBackground = selectMiniPieBackground(chart);
  const activeRoomColoring = useAppSelector(selectActiveRoomColoring);
  const roomColoringTrendAvailable = useAppSelector(selectTrendByKeyArgsForRoomColoring);

  if (!activeRoomColoring) {
    return (
      <Card withBorder radius="sm" p="xs">
        <Text size="xs" c="dimmed">
          Ingen romfargelegging er aktiv.
        </Text>
      </Card>
    );
  }

  if (collapsible && collapsed) {
    return (
      <button
        type="button"
        className="room-chart-mini"
        onClick={toggle}
        aria-label={`Vis store diagrammer: ${activeRoomColoring.title}`}
        aria-expanded={false}
        style={{ ...style, width: "min(420px, calc(100% - 75px))", height: "auto" }}
      >
        <span
          className="room-chart-mini-pie"
          aria-hidden="true"
          style={{ background: miniPieBackground }}
        />
        <span className="room-chart-mini-title">
          <strong>{activeRoomColoring.title}</strong>
          <span>{totalCount} rom · Vis diagrammer</span>
        </span>
        {roomColoringTrendAvailable && (
          <span className="room-chart-mini-trend" aria-hidden="true">
            <RoomColoringTrendChart compact />
          </span>
        )}
        <span aria-hidden="true">⌃</span>
      </button>
    );
  }

  return (
    <Card
      withBorder
      radius="sm"
      p="xs"
      style={{
        background: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(6px)",
        ...(collapsible ? { maxWidth: "calc(100% - 75px)" } : {}),
        ...style,
      }}
    >
      {/* ✅ Important: make Stack fill card and allow children to flex */}
      <Stack h="100%" style={{ minHeight: 0 }} gap={6}>
        <Group justify="space-between" align="center" wrap="nowrap" style={{ flexShrink: 0 }}>
          <Text size="sm" fw={600} truncate style={{ minWidth: 0 }}>
            {activeRoomColoring.title}
          </Text>

          {collapsible && (
            <button
              type="button"
              className="room-chart-collapse"
              onClick={toggle}
              aria-label="Minimer diagrammer"
              aria-expanded={true}
            >
              ⌄ <span>Minimer</span>
            </button>
          )}
        </Group>

        {/* ✅ Divider should not steal flex height */}
        <Divider style={{ flexShrink: 0 }} />

        {/* ✅ THIS wrapper must be flex:1 (not just the Flex component) */}
        <Box style={{ flex: 1, minHeight: 0 }}>
          <Flex h="100%" gap="md" align="stretch">
            <Box style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
              <RoomColoringPieChart />
            </Box>

            {roomColoringTrendAvailable && (
              <Box style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                <RoomColoringTrendChart />
              </Box>
            )}
          </Flex>
        </Box>
      </Stack>
    </Card>
  );
};
